import type { StoryObj } from '@storybook/html'
import type { CobaltCheckbox as CobaltCheckboxProps } from './cobalt-checkbox'

export default {
  title: 'Components/Form/cobalt-checkbox',
  argTypes: {
    label: {
      type: { name: 'string', required: true },
      description: 'This is the text which accompany the checkbox. It is **required** for accessibility concerns.'
    },
    format: {
      control: 'select',
      options: ['right', 'left', 'hidden'],
      description: 'A string indicating the position of the label or if it is hidden.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'right' }
      }
    },
    checked: {
      control: 'boolean',
      options: [true, false],
      description: 'A Boolean indicating whether the box is checked or unchecked. It should be false if the checkbox is `indeterminate`.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    disabled: {
      control: 'boolean',
      options: [true, false],
      description: 'A boolean indicating the disable state of the checkbox. The `aria-disabled` attribute relies on this property.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    cobaltCheckboxChange: {
      description: 'Custom event emitted when checkbox is checked or not',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltCheckboxChange={handleChange}\nVue:@cobaltCheckboxChange="handleChange"\nAngular:(cobaltCheckboxChange)="handleChange()"\nVanillaJs:.addEventListener("cobaltCheckboxChange", (event) => {//handle change});'
        }
      },

      action: 'cobaltCheckboxChange'
    }
  }
}

const TemplateDefault = (args: CobaltCheckboxProps) => {
  return `
<cobalt-checkbox label="${args.label}" format="${args.format}" checked="${args.checked}" disabled="${args.disabled}"></cobalt-checkbox>
  `
}

export const Default: StoryObj = TemplateDefault.bind({})

Default.args = {
  label: 'Label',
  format: 'right',
  checked: false,
  disabled: false
}

const TemplateIndeterminate = (args: CobaltCheckboxProps) => {
  return `
<cobalt-checkbox id="checkbox-indeterminate" label="${args.label}" indeterminate="${args.indeterminate}" checked="false"></cobalt-checkbox>
  `
}

export const Indeterminate: StoryObj = TemplateIndeterminate.bind({})

Indeterminate.args = {
  label: 'Label',
  indeterminate: true
}

const TemplateDescription = (args: CobaltCheckboxProps) => {
  return `
<cobalt-checkbox label="${args.label}" description="${args.description}"></cobalt-checkbox>
  `
}

export const WithDescription: StoryObj = TemplateDescription.bind({})

WithDescription.args = {
  label: 'Label',
  description: 'A description'
}

const TemplateOnChangeEvent = (args: CobaltCheckboxProps) => {
  return `
<script>
  document.querySelector('#checkbox-on-change').addEventListener('cobaltCheckboxChange', (e) => {
    document.querySelector('#checkbox-checked').textContent = JSON.stringify(e.detail)
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <p class="typography-body-medium-regular">Checkbox is checked: <span id="checkbox-checked">{"value":"${args.value}","checked":false}</span></p>
  <cobalt-checkbox id="checkbox-on-change" label="${args.label}" value="${args.value}"></cobalt-checkbox>
</div>
`
}

export const OnChangeEvent: StoryObj = TemplateOnChangeEvent.bind({})

OnChangeEvent.args = {
  label: 'Label',
  value: 'value'
}
